import { useQuery } from "@tanstack/react-query";
import { GET_MY_PRODUCTS } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { useReactQueryErrorReporter } from "./use-error-reporter";
import { hasAuthToken } from "../utils/tokenUtils";
import type { MyProduct } from "../types/products";

export type { MyProduct };

/**
 * The raw shape off the wire. `sections` and the schedule fields are nullable on
 * the schema so that a resolver deployed behind the schema can't null out the
 * whole product list; they are normalised here and nowhere else. A resolver
 * older than the schema omits them entirely, which `undefined` covers.
 */
type RawMyProduct = Omit<
  MyProduct,
  "sections" | "startDate" | "endDate" | "gracePeriodDays"
> & {
  sections?: MyProduct["sections"] | null;
  startDate?: string | null;
  endDate?: string | null;
  gracePeriodDays?: number | null;
};

interface GetMyProductsResponse {
  getMyProducts: RawMyProduct[];
}

/**
 * Loads the products the signed-in user is entitled to (derived server-side
 * from their enrollments' `productCodes`), each enriched with the product's
 * learner/educator plan codes. Distinct from `useProducts`, which loads the
 * full published product catalog from S3.
 */
export const useMyProducts = () => {
  const { data, error, isLoading, refetch } = useQuery<MyProduct[]>({
    queryKey: ["myProducts"],
    queryFn: async () => {
      const resp =
        await graphQLClient.request<GetMyProductsResponse>(GET_MY_PRODUCTS);
      return (resp.getMyProducts ?? []).map((product) => ({
        ...product,
        sections: product.sections ?? [],
        // A missing schedule is "always current", which is what null means to
        // productAvailability — so normalise undefined away at the boundary.
        startDate: product.startDate ?? null,
        endDate: product.endDate ?? null,
        gracePeriodDays: product.gracePeriodDays ?? null,
      }));
    },
    // Nothing to resolve without credentials — keeps public screens (login,
    // maintenance takeover) from firing an unauthenticated request.
    enabled: hasAuthToken(),
  });

  useReactQueryErrorReporter("Could not load your products", error, false);

  return { products: data ?? [], loading: isLoading, error, refetch };
};
